/*
 * AN45° — Offer ladder (homepage rail + /pages/offers)
 * ---------------------------------------------------------------------------
 * Renders the free-gift ladder as cards and wires an "Add to unlock" button per
 * rung. Paired with sections/an45-offer-ladder.liquid.
 *
 * SINGLE SOURCE OF TRUTH. Every offer shown here is read from the SAME JSON the
 * cart engine allocates from (<script id="an45-gift-config">, produced by
 * snippets/an45-gift-config.liquid). Nothing about an offer — its trigger, its
 * gifts, its wording — is restated in section settings or in this file. That is
 * deliberate: the store previously described the same promo in the ads, the cart
 * engine and the marketing copy, and they drifted. To change what this renders,
 * edit the offers array in the config snippet. See WEBSITE.md §10.
 *
 * We also reuse the engine's own predicate (window.AN45GiftEngine.evalCond) to
 * decide "unlocked" vs "locked", so a rung can never claim a gift the cart
 * wouldn't actually award.
 *
 * This module NEVER touches gift lines. It adds TRIGGER products only; the gift
 * engine's reconcile() then adds/removes the $0 gift products as it always has.
 */
(function () {
  'use strict';

  var LADDER_SEL = '[data-an45-offer-ladder]';

  /* ======================================================================
   * PURE LOGIC (no DOM / no network — unit-tested in scripts/test-offer-ladder.mjs)
   * ==================================================================== */

  /*
   * What "Add to unlock" should put in the cart for one rung.
   * Walks the offer's `unlock` list in order, adding one unit at a time, and
   * STOPS the moment the engine's own predicate says the offer is satisfied —
   * so the button never over-adds. Consequences worth keeping:
   *   - empty cart + sun-2  -> [tinted, non-tinted]  (a real combo, not 2x one SKU)
   *   - cart has tinted     -> [non-tinted] only
   *   - offer already earned-> []                    (button renders as "Unlocked")
   * `engine` is window.AN45GiftEngine (or the Node export) — we borrow its
   * evalCond/qtyDelta so display and allocation can never disagree.
   */
  function unlockPlan(offer, qty, engine) {
    var list = (offer && offer.unlock) || (offer && offer.nudge && offer.nudge.try) || [];
    var base = qty || {};

    function apply(items) {
      var sim = base;
      for (var i = 0; i < items.length; i++) sim = engine.qtyDelta(sim, items[i], 1);
      return sim;
    }

    // 1. Prefer products the shopper does NOT already own, keeping declared order
    // among equals. Otherwise a cart holding one Tinted would be offered a SECOND
    // Tinted (which does satisfy "2 sunscreens", but duplicates a SKU they already
    // have instead of completing the combo). Owner's rule: the 2-sunscreen tiers
    // build one Tinted + one Hyaluronic.
    var candidates = list.map(function (h, i) {
      return { h: h, owned: (base[h] || 0) > 0 ? 1 : 0, i: i };
    }).sort(function (a, b) {
      return a.owned - b.owned || a.i - b.i;
    }).map(function (x) { return x.h; });

    // 2. Accumulate until the rule is satisfied.
    var need = [];
    for (var i = 0; i < candidates.length; i++) {
      if (engine.evalCond(offer.when, apply(need))) break;
      need.push(candidates[i]);
    }
    // If the declared list still doesn't satisfy the rule, add the whole list
    // rather than a partial basket that unlocks nothing.
    if (!engine.evalCond(offer.when, apply(need))) return list.slice();

    // 3. Prune. Ordered accumulation alone over-adds whenever an EARLIER part of
    // the rule is already covered by something the cart holds. Concretely: a
    // shopper who owns the Sunscreen Duo asks to unlock sun-3 ("2 sunscreens +
    // shampoo") — step 1 walks past tinted and non-tinted before reaching the
    // shampoo, and would charge them for two sunscreens they do not need. Drop
    // any item the offer still succeeds without, back to front so the last
    // (rule-completing) item survives.
    for (var j = need.length - 1; j >= 0; j--) {
      var trial = need.slice();
      trial.splice(j, 1);
      if (engine.evalCond(offer.when, apply(trial))) need = trial;
    }
    return need;
  }

  // Node export for tests (mirrors assets/an45-gift-engine.js).
  if (typeof module !== 'undefined' && module.exports) module.exports = { unlockPlan: unlockPlan };
  if (typeof document === 'undefined') return;

  /* ---------- boot ordering (load-bearing) ----------
   * sections/an45-offer-ladder.liquid emits <script src=… defer> INSIDE <main>,
   * but layout/theme.liquid renders the gift config + engine at the END of
   * <body>. Deferred scripts run in DOCUMENT ORDER, so this file executes BEFORE
   * window.AN45GiftEngine exists. Booting on readyState alone therefore hit the
   * dependency guard and left only the fallback line on screen (live bug, found
   * 2026-08-05 — the section rendered as a blank slate of text).
   *
   * DOMContentLoaded fires only after EVERY deferred script has run, so waiting
   * for it is what actually orders us behind the engine. The bounded retry is a
   * guard in case the engine is ever switched to async/module loading.
   */
  var booted = false;

  function tryBoot() {
    if (booted) return true;
    if (!document.getElementById('an45-gift-config') || !window.AN45GiftEngine) return false;
    booted = true;
    init();
    return true;
  }

  function bootWithRetry(triesLeft) {
    if (tryBoot() || triesLeft <= 0) {
      if (!booted && triesLeft <= 0) {
        console.warn('[an45-offer-ladder] gift config/engine never appeared — leaving the fallback copy in place');
      }
      return;
    }
    setTimeout(function () { bootWithRetry(triesLeft - 1); }, 100);
  }

  function ready(fn) {
    if (document.readyState === 'complete') { fn(); return; }
    document.addEventListener('DOMContentLoaded', fn, { once: true });
    window.addEventListener('load', fn, { once: true }); // belt-and-braces; tryBoot is idempotent
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function init() {
    var roots = document.querySelectorAll(LADDER_SEL);
    if (!roots.length) return;

    var cfgEl = document.getElementById('an45-gift-config');
    var engine = window.AN45GiftEngine;
    // The config + engine only render when settings.cart_type == 'drawer' (see
    // layout/theme.liquid). Without them we cannot render a truthful ladder, so
    // we leave the server-rendered fallback copy in place rather than guess.
    if (!cfgEl || !engine) return;

    var config;
    try { config = JSON.parse(cfgEl.textContent); } catch (e) {
      console.warn('[an45-offer-ladder] bad config JSON', e);
      return;
    }
    if (!config.offers || !config.offers.length) return;

    // Ladder order = the order the offers are declared in the config, which is
    // already authored low tier -> high tier per track. Only offers carrying a
    // `label` are marketing-visible, so an internal-only rule can be added to
    // the engine without silently appearing on the storefront.
    var offers = config.offers.filter(function (o) { return o.label; });
    if (!offers.length) return;


    var state = { qty: {}, loaded: false };

    /* ---------- cart state ---------- */
    var giftVariantIds = {};
    Object.keys(config.gifts || {}).forEach(function (h) {
      var g = config.gifts[h];
      if (g && g.id) giftVariantIds[g.id] = h;
    });

    function fetchQty() {
      var routes = window.routes || {};
      return fetch((routes.cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var qty = {};
          (cart.items || []).forEach(function (it) {
            if (giftVariantIds[it.id]) return; // gift lines are not basket signal
            qty[it.handle] = (qty[it.handle] || 0) + it.quantity;
          });
          return qty;
        })
        .catch(function () { return {}; });
    }

    function planFor(offer, qty) { return unlockPlan(offer, qty, engine); }

    function isEarned(offer, qty) {
      return engine.evalCond(offer.when, qty);
    }

    /* ---------- render ---------- */
    function giftCard(handle) {
      var g = config.gifts[handle];
      if (!g) return '';
      var name = String(g.title || handle).replace(/^AN45[°º]?\s*/, '');
      return '<li class="an45-ol__gift">' +
        (g.image ? '<img class="an45-ol__gift-img" src="' + esc(g.image) + '" alt="" loading="lazy" width="120" height="120">' : '') +
        '<span class="an45-ol__gift-name">' + esc(name) + '</span>' +
        '</li>';
    }

    function cardHTML(offer, qty, index) {
      var earned = isEarned(offer, qty);
      var gifts = offer.gifts || [];
      var plan = planFor(offer, qty);
      var canAdd = plan.length > 0 && plan.every(function (h) {
        var p = config.products[h];
        return p && p.id && p.available !== false;
      });

      var h = '';
      // The flag is DATA now (`offer.flag`), not derived from the gift count. Counting
      // meant the haircare duo could never be flagged despite awarding the single most
      // valuable gift — see the schema note in an45-gift-config.liquid.
      var flag = offer.flag;
      h += '<article class="an45-ol__card' + (earned ? ' is-earned' : '') + (flag ? ' is-best' : '') +
        '" data-offer-id="' + esc(offer.id) + '" style="--i:' + index + '">';

      if (flag) h += '<span class="an45-ol__flag">' + esc(flag) + '</span>';
      // Starburst — the "something is happening" cue that reads before any copy does.
      // `gift_noun` lets one offer say "Premium gift" instead of the plain count.
      var noun = offer.gift_noun || (gifts.length > 1 ? 'Free gifts' : 'Free gift');
      h += '<span class="an45-ol__burst"><b>' + gifts.length + '</b><span>' + esc(noun) + '</span></span>';

      // Product photo for what you BUY (the gift thumbnails below show what you GET).
      // `hero` is a trigger-product handle, so the image resolves from the same
      // products map the Add-to-unlock button uses — no second source of truth.
      var hero = offer.hero ? config.products[offer.hero] : null;
      if (hero && hero.image) {
        h += '<div class="an45-ol__hero">' +
          '<img src="' + esc(hero.image) + '" alt="' + esc(hero.title || '') + '" loading="lazy" decoding="async" width="240" height="240">' +
          '</div>';
      }

      h += '<div class="an45-ol__top">';
      h += '<span class="an45-ol__step">Offer ' + (index + 1) + '</span>';
      // Only the earned state needs a pill now — the starburst already shouts the count.
      if (earned) h += '<span class="an45-ol__state an45-ol__state--on">Unlocked</span>';
      h += '</div>';

      h += '<h3 class="an45-ol__label">' + esc(offer.label) + '</h3>';
      if (offer.blurb) h += '<p class="an45-ol__blurb">' + esc(offer.blurb) + '</p>';

      // "You get" + the FREE pill share one row — as separate rows they left a dead
      // gap on cards with only one gift (Offer 1 next to Offer 3's three).
      h += '<div class="an45-ol__gets-head">' +
        '<span class="an45-ol__you-get">You get</span>' +
        '<span class="an45-ol__free">Free</span>' +
        '</div>';
      h += '<ul class="an45-ol__gifts">' + gifts.map(giftCard).join('') + '</ul>';

      h += '<div class="an45-ol__act">';
      if (earned) {
        h += '<p class="an45-ol__earned-msg">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>' +
          'Already in your cart</p>';
      } else if (canAdd) {
        h += '<button type="button" class="an45-ol__btn" data-an45-unlock="' + esc(offer.id) + '">Shop with the offer</button>';
      } else {
        h += '<a class="an45-ol__btn an45-ol__btn--alt" href="/collections/all">Shop the range</a>';
      }
      // Secondary route: read about the product before committing to the bundle.
      if (hero && hero.url) {
        h += '<a class="an45-ol__link" href="' + esc(hero.url) + '">View product details</a>';
      }
      h += '</div>';

      h += '</article>';
      return h;
    }

    /* The terms line is SERVER-rendered, and only when the section has a `terms`
     * setting (sections/an45-offer-ladder.liquid). A section with none — the homepage
     * rail — therefore printed nothing at all, disclaimer included. Inject one from
     * TERMS when the server left it out. Idempotent: it checks for the existing node,
     * so repainting on every cart change cannot stack duplicates. */
    function ensureTerms(root) {
      if (!TERMS) return;
      var host = root.closest('.an45-ol') || root.parentNode;
      if (!host || host.querySelector('.an45-ol__terms')) return;
      var wrap = host.querySelector('.an45-ol__wrap');
      if (!wrap || !wrap.parentNode) return;
      var p = document.createElement('p');
      p.className = 'an45-ol__terms';
      p.textContent = TERMS;
      wrap.parentNode.insertBefore(p, wrap.nextSibling);
    }

    function paint() {
      roots.forEach(function (root) {
        var track = root.querySelector('[data-an45-offer-track]');
        if (!track) return;
        track.innerHTML = offers.map(function (o, i) { return cardHTML(o, state.qty, i); }).join('');
        root.classList.add('is-ready');
        ensureTerms(root);
        bind(root);
      });
      paintModal(); // keep an open popup's "Already in your cart" honest as the cart changes
      highlightFromUrl();
    }

    /* ---------- add to unlock ----------
     * Mirrors the card ATC in sections/an45-products.liquid (which itself mirrors
     * Dawn's product-form.js): a FormData add carrying `sections`, then
     * cart.renderContents(response) — that re-renders AND opens the drawer in one
     * step. Do not "simplify" this to a plain JSON /cart/add.js: without the
     * sections round-trip the drawer opens showing stale contents. */
    function addItems(handles) {
      var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

      var cfg = (typeof fetchConfig === 'function')
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/javascript' } };
      cfg.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete cfg.headers['Content-Type']; // let the browser set the multipart boundary

      var body = new FormData();
      handles.forEach(function (h, i) {
        body.append('items[' + i + '][id]', config.products[h].id);
        body.append('items[' + i + '][quantity]', 1);
      });
      if (cart && cart.getSectionsToRender) {
        body.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
        body.append('sections_url', window.location.pathname);
        if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
      }
      cfg.body = body;

      var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      return fetch(addUrl, cfg)
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (response.status) throw new Error(response.description || 'add failed');
          // source:'product-form' is what the gift engine and the Duo bundle-swap
          // already listen for — reuse it so the $0 gift lines reconcile.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: response });
          }
          if (cart && cart.renderContents) cart.renderContents(response); // re-renders + opens
          return response;
        });
    }

    function bind(root) {
      root.querySelectorAll('[data-an45-unlock]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          var offer = offers.filter(function (o) { return o.id === btn.getAttribute('data-an45-unlock'); })[0];
          if (!offer) return;
          var plan = planFor(offer, state.qty);
          if (!plan.length) return;

          var original = btn.textContent;
          btn.disabled = true;
          btn.textContent = 'Adding…';

          addItems(plan).then(function () {
            return refresh(); // repaint the rungs so this one now reads "Unlocked"
          }).catch(function () {
            btn.disabled = false;
            btn.textContent = original;
          });
        });
      });
    }

    /* ======================================================================
     * OFFER POPUP (added 2026-08-16)
     * ----------------------------------------------------------------------
     * The artwork rail (sections/an45-offer-creatives.liquid) sells the offer but
     * its copy is baked into the JPEG; its cards linked to /pages/offers?o=<id>,
     * which did nothing when you were ALREADY on that page (owner, 2026-08-16).
     * They now open this dialog instead — the same card the ladder renders, from
     * the same config, so the popup can't misquote an offer either.
     *
     * It is built here, not in the creatives section, for one reason: everything
     * it shows (labels, gifts, the working add-to-cart) already lives in this
     * file. A second implementation over there would be the exact drift this
     * whole feature exists to prevent.
     *
     * Adding an offer goes through addItems() — real /cart/add.js on real, PAID
     * trigger products, so it still reports a Meta AddToCart. Only the $0 gift
     * lines take the silent route (see assets/an45-gift-engine.js).
     * ==================================================================== */
    // `data-terms` sits on the <section>. In popup layout the root IS that section;
    // in rail/grid the root is the inner wrap, so walk up for it.
    var TERMS = '';
    roots.forEach(function (r) {
      if (TERMS) return;
      var host = r.hasAttribute('data-terms') ? r : r.closest('[data-terms]');
      if (host) TERMS = host.getAttribute('data-terms') || '';
    });

    /* Fall back to the gift config's disclaimer when a section has no `terms` setting
     * (2026-09-21). This is not belt-and-braces — it fixed a live hole: /pages/offers
     * carried "…Gift colours may vary slightly from the images shown." in its setting
     * while the HOMEPAGE rail's setting was an empty string, so the rail AND the offer
     * detail modal opened from it showed no terms at all. The homepage is where most
     * people meet these offers.
     *
     * A section's own `terms` still WINS, so /pages/offers keeps its fuller sentence
     * (auto-add + one per order + stocks + colour). The config string is the floor,
     * not an override — and it is the only copy of the colour line, so editing it in
     * the snippet moves the cart drawer, both ladders, the modal and the PDP cards
     * together. */
    if (!TERMS) TERMS = (config.policies && config.policies.gift_disclaimer) || '';

    var modal = null;
    var modalOfferId = null;
    var lastTrigger = null;

    function buildModal() {
      if (modal) return modal;
      modal = document.createElement('div');
      modal.className = 'an45-om';
      modal.setAttribute('data-an45-offer-modal', '');
      modal.hidden = true;
      modal.innerHTML =
        '<div class="an45-om__scrim" data-an45-om-close></div>' +
        '<div class="an45-om__dialog" role="dialog" aria-modal="true" aria-labelledby="an45-om-title" tabindex="-1">' +
          '<button type="button" class="an45-om__x" data-an45-om-close aria-label="Close">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">' +
              '<line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>' +
          '</button>' +
          '<div class="an45-om__body"></div>' +
        '</div>';
      document.body.appendChild(modal);

      modal.addEventListener('click', function (e) {
        if (e.target && e.target.closest && e.target.closest('[data-an45-om-close]')) closeOffer();
      });
      return modal;
    }

    function modalGift(handle) {
      var g = config.gifts[handle];
      if (!g) return '';
      var name = String(g.title || handle).replace(/^AN45[°º]?\s*/, '');
      return '<li class="an45-om__gift">' +
        (g.image ? '<img src="' + esc(g.image) + '" alt="" loading="lazy" width="46" height="46">' : '') +
        '<span class="an45-om__gift-name">' + esc(name) + '</span></li>';
    }

    function modalHTML(offer, qty) {
      var earned = isEarned(offer, qty);
      var gifts = offer.gifts || [];
      var plan = planFor(offer, qty);
      var canAdd = plan.length > 0 && plan.every(function (h) {
        var p = config.products[h];
        return p && p.id && p.available !== false;
      });
      var hero = offer.hero ? config.products[offer.hero] : null;
      var noun = offer.gift_noun || (gifts.length > 1 ? 'Free gifts' : 'Free gift');

      /* The DETAIL view shows the offer's AD ARTWORK, not the hero product photo
         (owner, 2026-09-09): opening "Sunscreen combo + Shampoo & Conditioner" on a
         picture of a single shampoo reads like the offer is about that one bottle.
         The card in the rail keeps the product photo — that is the 2026-08-05 "picture
         what you BUY" decision — but the brief you open shows the same image the
         homepage creative rail does. Falls back to the hero photo if the artwork is
         missing, so an un-uploaded creative degrades instead of leaving a blank panel. */
      var art = offer.creative && config.creatives ? config.creatives[offer.creative] : null;

      var h = '';
      if (art) {
        h += '<div class="an45-om__hero an45-om__hero--art"><img src="' + esc(art) + '" alt="' +
          esc(offer.label || '') + '" decoding="async" loading="lazy"></div>';
      } else if (hero && hero.image) {
        h += '<div class="an45-om__hero"><img src="' + esc(hero.image) + '" alt="' + esc(hero.title || '') +
          '" width="190" height="190" decoding="async"></div>';
      }
      h += '<div class="an45-om__main">';
      h += '<span class="an45-om__burst" aria-hidden="true"><b>' + gifts.length + '</b><span>' + esc(noun) + '</span></span>';
      if (offer.flag) h += '<span class="an45-om__flag">' + esc(offer.flag) + '</span>';
      h += '<h2 class="an45-om__title" id="an45-om-title">' + esc(offer.label) + '</h2>';
      if (offer.blurb) h += '<p class="an45-om__blurb">' + esc(offer.blurb) + '</p>';
      h += '<div class="an45-om__gets-head"><span class="an45-om__you-get">You get</span>' +
        '<span class="an45-om__free">' + esc(noun) + '</span></div>';
      h += '<ul class="an45-om__gifts">' + gifts.map(modalGift).join('') + '</ul>';

      h += '<div class="an45-om__act">';
      if (earned) {
        h += '<p class="an45-om__earned">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>' +
          'Already in your cart</p>';
      } else if (canAdd) {
        h += '<button type="button" class="an45-om__btn" data-an45-om-unlock="' + esc(offer.id) + '">Shop with the offer</button>';
      } else {
        h += '<a class="an45-om__btn" href="/collections/all">Shop the range</a>';
      }
      if (hero && hero.url) {
        h += '<a class="an45-om__btn an45-om__btn--ghost" href="' + esc(hero.url) + '">View product details</a>';
      }
      h += '</div>';
      if (TERMS) h += '<p class="an45-om__terms">' + esc(TERMS) + '</p>';
      h += '</div>';
      return h;
    }

    function paintModal() {
      if (!modal || modal.hidden || !modalOfferId) return;
      var offer = offers.filter(function (o) { return o.id === modalOfferId; })[0];
      if (!offer) return;
      modal.querySelector('.an45-om__body').innerHTML = modalHTML(offer, state.qty);
    }

    function onModalKeydown(e) {
      if (!modal || modal.hidden) return;
      if (e.key === 'Escape') { closeOffer(); return; }
      if (e.key !== 'Tab') return;
      // Focus trap: keep Tab inside the dialog while it owns the screen.
      var dialog = modal.querySelector('.an45-om__dialog');
      var f = dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    function openOffer(id, trigger) {
      var offer = offers.filter(function (o) { return o.id === id; })[0];
      if (!offer) return false;
      buildModal();
      modalOfferId = id;
      lastTrigger = trigger || null;
      modal.hidden = false;
      paintModal();
      document.documentElement.style.overflow = 'hidden'; // don't scroll the page behind the sheet
      document.addEventListener('keydown', onModalKeydown);
      modal.querySelector('.an45-om__dialog').focus();
      // The cart may have changed since the last paint (another tab, the drawer);
      // re-read it so "Already in your cart" is truthful the moment it is shown.
      refresh();
      return true;
    }

    function closeOffer() {
      if (!modal || modal.hidden) return;
      modal.hidden = true;
      modalOfferId = null;
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onModalKeydown);
      if (lastTrigger && lastTrigger.focus) lastTrigger.focus();
      lastTrigger = null;
    }

    // Delegated: survives the creatives rail being re-rendered by the theme editor.
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      var unlock = t.closest('[data-an45-om-unlock]');
      if (unlock) {
        if (unlock.disabled) return;
        var offer = offers.filter(function (o) { return o.id === unlock.getAttribute('data-an45-om-unlock'); })[0];
        if (!offer) return;
        var plan = planFor(offer, state.qty);
        if (!plan.length) return;
        var original = unlock.textContent;
        unlock.disabled = true;
        unlock.textContent = 'Adding…';
        addItems(plan).then(function () {
          closeOffer();          // the drawer is already open on top; get out of its way
          return refresh();
        }).catch(function () {
          unlock.disabled = false;
          unlock.textContent = original;
        });
        return;
      }

      // Artwork card -> popup. Only for links whose ?o= names a real offer, so the
      // creative that points straight at a product page still navigates normally.
      var a = t.closest('a[href]');
      if (!a) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return; // keep open-in-new-tab
      if (a.target && a.target !== '_self') return;
      var oid;
      try { oid = new URL(a.href, window.location.origin).searchParams.get('o'); } catch (err) { return; }
      if (!oid) return;
      if (!offers.some(function (o) { return o.id === oid; })) return;
      if (openOffer(oid, a)) e.preventDefault();
    });

    /* ---------- deep link (?o=sun-2 from a Meta ad) ---------- */
    // paint() runs on EVERY cart update, so this must act only once: without the
    // latch, adding to the cart (or the gift engine reconciling) would re-open a
    // popup the shopper had just closed, on a URL that still carries ?o=.
    var deepLinkDone = false;
    function highlightFromUrl() {
      if (deepLinkDone) return;
      var id;
      try { id = new URLSearchParams(window.location.search).get('o'); } catch (e) { return; }
      if (!id) return;
      var card = document.querySelector('.an45-ol__card[data-offer-id="' + id.replace(/"/g, '') + '"]');
      // No card on the page = popup layout. A ?o= link is an explicit request for ONE
      // offer (a Meta ad, or the artwork rail on another page), so show it directly —
      // otherwise the deep link would land on a page with nothing to highlight, which
      // is the "clicking does nothing" bug all over again.
      if (!card) { deepLinkDone = true; openOffer(id, null); return; }
      deepLinkDone = true;
      card.classList.add('is-focus');
      // Position the matching card INSIDE the rail only. We deliberately do NOT
      // scroll the page to the section: on a phone that yanked the viewport past
      // the creatives down to the ladder the moment the page opened, which read as
      // the page moving on its own (owner feedback, 2026-08-05). The highlight is
      // enough to mark the right rung once the shopper scrolls there themselves.
      var rail = card.closest('[data-an45-offer-track]');
      if (rail && rail.scrollWidth > rail.clientWidth) {
        rail.scrollTo({ left: card.offsetLeft - 16, behavior: 'smooth' });
      }
    }

    /* ---------- lifecycle ---------- */
    function refresh() {
      return fetchQty().then(function (qty) {
        state.qty = qty;
        state.loaded = true;
        paint();
      });
    }

    refresh();

    // Re-paint whenever the cart changes anywhere (PDP add, drawer edit, the gift
    // engine's own reconcile) so "Unlocked" ticks are always truthful.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { refresh(); });
    }
  }

  ready(function () { bootWithRetry(30); }); // ~3s of grace, then give up quietly
})();
